import * as React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Text,
  Caption1,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  Document20Regular,
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
} from "@fluentui/react-icons";
import {
  ClientDocumentDto,
  getClientDocuments,
} from "../../../services/client-documents.service";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "550px",
    minWidth: "400px",
  },
  documentList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    maxHeight: "350px",
    overflowY: "auto",
  },
  documentItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
    transition: "all 0.15s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
  },
  documentItemSelected: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
    cursor: "pointer",
  },
  documentInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  documentMeta: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
  },
  documentIcon: {
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  selectedIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXL,
  },
  emptyState: {
    padding: tokens.spacingVerticalXL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
    textAlign: "center",
  },
  tagList: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    flexWrap: "wrap",
  },
  tag: {
    fontSize: tokens.fontSizeBase100,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground2,
  },
});

interface DocumentPickerDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  clientId: string;
  currentDocumentId?: string;
  onSelect: (documentId: string | null) => void;
}

/**
 * Modal for picking or uploading a client document to link to a status item.
 * Lists the client's documents with tag filters; an upload tab creates a new
 * document and links it in one step.
 */
const DocumentPickerDialog: React.FC<DocumentPickerDialogProps> = ({
  isOpen,
  onDismiss,
  clientId,
  currentDocumentId,
  onSelect,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const [documents, setDocuments] = useState<ClientDocumentDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(currentDocumentId || null);

  useEffect(() => {
    if (isOpen) {
      setSelectedId(currentDocumentId || null);
      fetchDocuments();
    }
  }, [isOpen, clientId]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getClientDocuments(clientId);
      setDocuments(data);
    } catch (err) {
      console.error("Failed to fetch client documents:", err);
      setError("Failed to load documents. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleConfirm = () => {
    onSelect(selectedId);
    onDismiss();
  };

  const handleUnlink = () => {
    onSelect(null);
    onDismiss();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_e, data) => !data.open && onDismiss()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={onDismiss}
              />
            }
          >
            Select Document
          </DialogTitle>

          <DialogContent>
            {error && (
              <div className={styles.errorMessage}>
                <Text>{error}</Text>
              </div>
            )}

            {isLoading && (
              <div className={styles.loadingContainer}>
                <Spinner size="medium" label="Loading documents..." />
              </div>
            )}

            {!isLoading && documents.length === 0 && !error && (
              <div className={styles.emptyState}>
                <Text>No documents found for this client.</Text>
              </div>
            )}

            {!isLoading && documents.length > 0 && (
              <div className={styles.documentList}>
                {documents.map((doc) => {
                  const isSelected = selectedId === doc.id;
                  return (
                    <div
                      key={doc.id}
                      className={isSelected ? styles.documentItemSelected : styles.documentItem}
                      onClick={() => setSelectedId(isSelected ? null : doc.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedId(isSelected ? null : doc.id);
                        }
                      }}
                    >
                      {isSelected ? (
                        <CheckmarkCircle20Regular className={styles.selectedIcon} />
                      ) : (
                        <Document20Regular className={styles.documentIcon} />
                      )}
                      <div className={styles.documentInfo}>
                        <Text className={typography.formLabel} truncate wrap={false}>
                          {doc.latestVersion?.fileName ?? doc.placeholderName ?? "Empty placeholder"}
                        </Text>
                        <div className={styles.documentMeta}>
                          {doc.latestVersion ? (
                            <>
                              <Caption1>{formatFileSize(doc.latestVersion.fileSize)}</Caption1>
                              <Caption1>v{doc.latestVersion.version}</Caption1>
                            </>
                          ) : (
                            <Caption1>No file uploaded yet</Caption1>
                          )}
                        </div>
                        {doc.tags.length > 0 && (
                          <div className={styles.tagList}>
                            {doc.tags.map((tag, i) => (
                              <span key={i} className={styles.tag}>
                                <strong>{tag.key}</strong>{tag.value ? `: ${tag.value}` : ""}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {currentDocumentId && (
              <Button
                appearance="subtle"
                icon={<DismissCircle20Regular />}
                onClick={handleUnlink}
              >
                Unlink
              </Button>
            )}
            <Button appearance="secondary" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={selectedId === (currentDocumentId || null)}
            >
              Select
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default DocumentPickerDialog;
